import React from 'react';
import { Input, type InputProps } from '../atoms/Input';

/**
 * FormField Component - Molecule Level
 * 
 * A complete form field with label, input, and validation messages.
 * Combines Label + Input + Error/Helper text.
 * 
 * Features:
 * - Label with optional required indicator
 * - Input field with all Input component features
 * - Error and helper text display
 * - Accessible (proper label association)
 */

export interface FormFieldProps extends InputProps {
  /** Label text for the field */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Additional label class names */
  labelClassName?: string;
  /** Whether to show the required asterisk */
  showRequiredIndicator?: boolean;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  required = false,
  labelClassName = '',
  showRequiredIndicator = true,
  id,
  error,
  helperText,
  className,
  containerClassName,
  ...inputProps
}) => {
  const fieldId = id || `field-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`w-full ${containerClassName || ''}`}>
      {label && (
        <label
          htmlFor={fieldId}
          className={`
            block text-sm font-medium text-left text-gray-700 mb-1.5
            ${labelClassName}
          `}
        >
          {label}
          {required && showRequiredIndicator && (
            <span className="text-red-500 ml-1" aria-label="required">
              *
            </span>
          )}
        </label>
      )}
      
      <Input
        id={fieldId}
        error={error}
        helperText={helperText}
        required={required}
        className={className}
        aria-required={required}
        {...inputProps}
      />
    </div>
  );
};

export default FormField;

